/**
 * A closed list's terms, in the reader's language.
 *
 * A term (`output_constraints.values`, in French) is the KEY a value is stored, corrected, filtered
 * and exported under, whatever language the dossier was written in or the reader reads — that is what
 * lets a cohort count one category across languages. Only its DISPLAY follows the reader: the API
 * serves, with each field's metadata (`PatientSchemaField.value_labels`,
 * `PathwayDimension.value_labels`), the labels of the request's locale, like it serves the field's
 * own name. Every place that SHOWS a value routes it through here; nothing else ever sees a label.
 *
 * A term without a label — French, an undeclared value, a speciality whose labels nobody wrote yet —
 * is shown as written.
 */

/** `term → label` for one field, in the reader's locale. Absent when the field labels nothing. */
export type ValueLabels = Readonly<Record<string, string>> | null | undefined;

/** The label `value` is shown under, or `value` itself. */
export function valueLabel(labels: ValueLabels, value: string): string {
  // Own keys only: a stored value « constructor » must not read an inherited property.
  if (!labels || !Object.prototype.hasOwnProperty.call(labels, value))
    return value;
  const label = labels[value];
  return typeof label === "string" && label.trim() ? label : value;
}

/**
 * `value` with each term it carries replaced by its label — a term, or a list of them. Anything
 * else (a number, a date, a score series, a problem list) is returned untouched: only a term has a
 * label, and a display that relabelled other shapes would invent one.
 */
export function labelledValue<T>(labels: ValueLabels, value: T): T {
  if (!labels) return value;
  if (typeof value === "string") return valueLabel(labels, value) as T;
  if (Array.isArray(value)) {
    return value.map((item) =>
      typeof item === "string" ? valueLabel(labels, item) : item,
    ) as T;
  }
  return value;
}

/** What `valueLabeller` needs of a dimension: its key, and its terms' labels. */
interface LabelledDimension {
  key: string;
  value_labels?: Record<string, string>;
}

/**
 * One function for a whole board: `(dimension key, value) → label`. Its values are terms of
 * several fields at once — a bar of the indication split by the act — so a label is looked up on
 * the dimension the value belongs to, never on whichever field happens to declare that spelling.
 */
export function valueLabeller(
  dimensions: readonly LabelledDimension[] | null | undefined,
): (dimension: string, value: string) => string {
  const byKey = new Map<string, ValueLabels>(
    (dimensions ?? []).map((dimension) => [
      dimension.key,
      dimension.value_labels,
    ]),
  );
  return (dimension, value) => valueLabel(byKey.get(dimension), value);
}
